import React, { createContext, JSX, PropsWithChildren, useContext, useState } from 'react';
import { SearchFilterContentTypes, SearchOrigins } from 'search-gateway-client';

import { SearchBarVariant } from '../elements/NavSearchElements/searchUtils';

export interface NavSearchProps {
    contentType: SearchFilterContentTypes;
    onSubmit: (contentType: SearchFilterContentTypes, value: string, searchOrigin: SearchOrigins) => void;
    userHasAnySubscription: boolean;
    isEnterpriseMember: boolean;
    pproPluginEnabled: boolean;
    isSearchAppLoaded: () => boolean;
    imagesUrl: string;
    showSuggestions: boolean;
    setShowSuggestions: (value: boolean) => void;
    searchOrigin?: SearchOrigins;
    useSnapshots?: boolean;
    searchBarVariant: SearchBarVariant;
    className?: string;
}

export interface NavSearchContextValue extends NavSearchProps {
    conversationalSearchEnabled: boolean;
    setConversationalSearchEnabled: React.Dispatch<React.SetStateAction<boolean>>;
}

export const NavSearchContext = createContext({} as NavSearchContextValue);

/**
 * Lightweight provider that only supplies conversational search state via NavSearchContext.
 * Use at a high level (e.g. SearchHero) so that components above the full NavSearchContextProvider
 * can still read conversationalSearchEnabled from context.
 */
export function ConversationalSearchProvider({ children }: PropsWithChildren): JSX.Element {
    const [conversationalSearchEnabled, setConversationalSearchEnabled] = useState(false);

    return (
        <NavSearchContext.Provider
            value={
                {
                    conversationalSearchEnabled,
                    setConversationalSearchEnabled,
                } as NavSearchContextValue
            }
        >
            {children}
        </NavSearchContext.Provider>
    );
}

/**
 * Full provider that supplies all search props. When nested inside a ConversationalSearchProvider,
 * inherits the conversational search state from the outer context; otherwise owns it locally.
 */
export function NavSearchContextProvider({ children, ...props }: PropsWithChildren<NavSearchProps>): JSX.Element {
    const outerContext = useContext(NavSearchContext);
    const [localEnabled, setLocalEnabled] = useState(false);

    const conversationalSearchEnabled = outerContext?.conversationalSearchEnabled ?? localEnabled;
    const setConversationalSearchEnabled = outerContext?.setConversationalSearchEnabled ?? setLocalEnabled;

    return (
        <NavSearchContext.Provider
            value={{
                ...props,
                conversationalSearchEnabled,
                setConversationalSearchEnabled,
            }}
        >
            {children}
        </NavSearchContext.Provider>
    );
}

/**
 * Convenience hook for components that only need the conversational search toggle.
 */
export function useConversationalSearch(): {
    conversationalSearchEnabled: boolean;
    setConversationalSearchEnabled: React.Dispatch<React.SetStateAction<boolean>>;
} {
    const { conversationalSearchEnabled, setConversationalSearchEnabled } = useContext(NavSearchContext);
    return { conversationalSearchEnabled, setConversationalSearchEnabled };
}
